/**
 * The card types this client renders. `CardSchemaCatalog` in the API is the
 * authority: it knows email, form, crm_update, brief and appointment, and
 * email_batch is carried ahead of its schema. A type with no schema cannot be
 * produced by any recipe, so it is not modelled here.
 */
export const ActionType = {
  Email: 'email',
  EmailBatch: 'email_batch',
  Form: 'form',
  CrmUpdate: 'crm_update',
  Brief: 'brief',
  Appointment: 'appointment',
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

export const ActionStatus = {
  Pending: 'pending',
  Executing: 'executing',
  Done: 'done',
  Rejected: 'rejected',
  Failed: 'failed',
} as const;

export type ActionStatus = (typeof ActionStatus)[keyof typeof ActionStatus];

export const OPEN_STATUSES: ReadonlySet<ActionStatus> = new Set<ActionStatus>([
  ActionStatus.Pending,
  ActionStatus.Executing,
]);

export function isOpen(status: ActionStatus): boolean {
  return OPEN_STATUSES.has(status);
}

export function isSettled(status: ActionStatus): boolean {
  return !OPEN_STATUSES.has(status);
}

export const ActionTrigger = {
  Manual: 'manual',
  Event: 'event',
  Schedule: 'schedule',
  Recipe: 'recipe',
  IqBoost: 'iq_boost',
} as const;

export type ActionTrigger = (typeof ActionTrigger)[keyof typeof ActionTrigger];

export const ItemDecision = {
  Accept: 'accept',
  Reject: 'reject',
} as const;

export type ItemDecision = (typeof ItemDecision)[keyof typeof ItemDecision];

export const ItemResult = {
  Done: 'done',
  Conflict: 'conflict',
  Failed: 'failed',
  Skipped: 'skipped',
} as const;

export type ItemResult = (typeof ItemResult)[keyof typeof ItemResult];

export interface ActionClient {
  readonly id: string;
  readonly name: string;
}

export interface ActionSource {
  readonly trigger: ActionTrigger;
  readonly label: string;
  readonly ref?: string;
}

export interface ActionDecision {
  readonly by: string;
  readonly at: string;
  readonly reason?: string;
  readonly items?: Readonly<Record<string, ItemDecision>>;
}

export interface Action<TPayload = unknown> {
  readonly id: string;
  readonly type: ActionType;
  readonly status: ActionStatus;
  readonly title: string;
  readonly summary: string;
  readonly client: ActionClient;
  readonly source: ActionSource;
  readonly recipe: string;
  readonly createdAt: string;
  readonly editable: readonly string[];
  readonly payload: TPayload;
  readonly decision?: ActionDecision;
  readonly result?: string;
  readonly failureReason?: string;
  readonly runId?: string;
}
